import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  ChevronLeft, ChevronRight, Check, CalendarCheck, Minus, Plus, MessageCircle, ArrowRight,
} from 'lucide-react';
import { db, submitStayRequest } from '@/lib/firebase';
import { useBusiness } from '@/lib/business';
import { waLink } from '@/data/site';
import { ROOMS, STAY_POLICY } from '@/data/accommodation';
import { Modal } from '@/components/ui/Modal';
import { formatPula } from '@/lib/money';
import type { BlockedRange } from '@/lib/types';

/* ── date helpers (local, YYYY-MM-DD) ── */
const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromYmd = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const niceDate = (s: string) => (s ? fromYmd(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—');
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Blocked dates for a single room id (includes ranges with no roomId = all rooms). */
function blockedSet(ranges: BlockedRange[], roomId: string): Set<string> {
  const set = new Set<string>();
  for (const r of ranges) {
    if (r.roomId && roomId && r.roomId !== roomId) continue;
    let d = fromYmd(r.from); const end = fromYmd(r.to);
    let guard = 0;
    while (d <= end && guard < 800) { set.add(ymd(d)); d = addDays(d, 1); guard++; }
  }
  return set;
}
/** Union of blocked dates across several rooms — a night is taken if ANY chosen room is blocked. */
function blockedUnion(ranges: BlockedRange[], roomIds: string[]): Set<string> {
  const set = new Set<string>();
  for (const id of roomIds) for (const s of blockedSet(ranges, id)) set.add(s);
  return set;
}
function monthCells(monthDate: Date): (Date | null)[] {
  const first = startOfMonth(monthDate);
  const startWeekday = (first.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = Array(startWeekday).fill(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(first.getFullYear(), first.getMonth(), day));
  return cells;
}

export function RoomBooking({ initialRoomId }: { initialRoomId?: string } = {}) {
  const biz = useBusiness();
  const todayStr = ymd(new Date());

  const [ranges, setRanges] = useState<BlockedRange[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>(
    initialRoomId && ROOMS.some((r) => r.id === initialRoomId) ? [initialRoomId] : (ROOMS[0] ? [ROOMS[0].id] : []),
  );
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [hp, setHp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    return onSnapshot(
      doc(db, 'availability', 'calendar'),
      (snap) => setRanges(snap.exists() ? ((snap.data().ranges as BlockedRange[]) || []) : []),
      () => setRanges([]),
    );
  }, []);

  const selectedRooms = useMemo(() => ROOMS.filter((r) => selectedIds.includes(r.id)), [selectedIds]);
  const blocked = useMemo(() => blockedUnion(ranges, selectedIds), [ranges, selectedIds]);
  const selectingEnd = Boolean(checkIn && !checkOut);
  const firstBlockedAfter = useMemo(
    () => (checkIn ? [...blocked].filter((s) => s > checkIn).sort()[0] : undefined),
    [blocked, checkIn],
  );

  // reset selection if it now overlaps a block (e.g. after changing rooms)
  useEffect(() => {
    if (checkIn && checkOut) {
      let d = fromYmd(checkIn); const end = fromYmd(checkOut);
      while (d < end) { if (blocked.has(ymd(d))) { setCheckIn(''); setCheckOut(''); break; } d = addDays(d, 1); }
    }
  }, [selectedIds]); // eslint-disable-line

  function toggleRoom(id: string) {
    setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  function disabled(d: Date): boolean {
    const s = ymd(d);
    if (s < todayStr) return true;
    if (blocked.has(s)) return true;
    if (selectingEnd) {
      if (s <= checkIn) return true;
      if (firstBlockedAfter && s > firstBlockedAfter) return true;
    }
    return false;
  }
  function pick(d: Date) {
    const s = ymd(d);
    if (!checkIn || (checkIn && checkOut)) { setCheckIn(s); setCheckOut(''); return; }
    if (s <= checkIn) { setCheckIn(s); setCheckOut(''); return; }
    setCheckOut(s);
  }

  const nights = checkIn && checkOut ? Math.round((fromYmd(checkOut).getTime() - fromYmd(checkIn).getTime()) / 86400000) : 0;
  const rate = selectedRooms.reduce((s, r) => s + r.price, 0); // combined per-night rate
  const total = rate * nights;
  const roomsLabel = selectedRooms.map((r) => r.name).join(' + ') || 'a room';

  function flash(m: string) { setError(m); }
  function openConfirm() {
    setError('');
    if (selectedRooms.length === 0) return flash('Please choose at least one room.');
    if (!checkIn || !checkOut || nights < 1) return flash('Please select your check-in and check-out dates.');
    if (!fullName.trim() || !email.trim()) return flash('Please add your name and email.');
    if (!phone.trim()) return flash('A phone number is required so we can confirm your booking.');
    setConfirming(true);
  }
  async function submit() {
    setError('');
    setConfirming(false);
    if (selectedRooms.length === 0) return;
    setBusy(true);
    try {
      const rooms = selectedRooms.map((r) => ({ roomId: r.id, roomName: r.name, price: r.price }));
      await submitStayRequest({
        hp, fullName: fullName.trim(), email: email.trim(), phone: phone.trim(),
        rooms,
        // legacy single-room fields (first room) so older tooling/emails still read something
        roomId: rooms[0].roomId, roomName: rooms.map((r) => r.roomName).join(', '),
        checkIn, checkOut, guests, nights, totalPrice: total, notes: notes.trim(),
      });
      setDone(true);
    } catch (e) {
      flash(e instanceof Error ? `${e.message} You can also request on WhatsApp.` : 'Something went wrong.');
    } finally { setBusy(false); }
  }

  const canPrev = startOfMonth(month) > startOfMonth(new Date());
  const waText = `Hi Grace Haven, I'd like to book ${roomsLabel} from ${niceDate(checkIn)} to ${niceDate(checkOut)} (${nights} night${nights === 1 ? '' : 's'}).`;

  if (done) {
    return (
      <div className="mt-16 border border-gold/50 bg-paper p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/20 text-gold-deep"><CalendarCheck size={30} /></div>
        <h2 className="mt-6 font-display text-3xl text-espresso">Booking requested</h2>
        <div className="rule-gold mx-auto mt-4" />
        <p className="mx-auto mt-5 max-w-md font-body text-muted">
          Thank you, {fullName.split(' ')[0] || 'and see you soon'}. We've emailed you a copy of your request for {selectedRooms.length > 1 ? `${selectedRooms.length} rooms (${roomsLabel})` : roomsLabel} ({niceDate(checkIn)} – {niceDate(checkOut)}, {nights} night{nights === 1 ? '' : 's'}, {formatPula(total)}). We'll confirm availability shortly — no payment is taken now.
        </p>
      </div>
    );
  }

  function renderMonth(m: Date) {
    return (
      <div className="flex-1">
        <p className="mb-3 text-center font-display text-lg text-espresso">{MONTHS[m.getMonth()]} {m.getFullYear()}</p>
        <div className="grid grid-cols-7 gap-1">
          {WEEKDAYS.map((w) => <div key={w} className="pb-1 text-center font-body text-[11px] font-semibold uppercase text-muted">{w}</div>)}
          {monthCells(m).map((d, i) => {
            if (!d) return <div key={i} />;
            const s = ymd(d);
            const dis = disabled(d);
            const isBlocked = blocked.has(s); // taken — distinct from merely past/out-of-range
            const isStart = s === checkIn, isEnd = s === checkOut;
            const inRange = checkIn && checkOut && s > checkIn && s < checkOut;
            const edge = isStart || isEnd;
            // Exactly one colour treatment per state (blocked reads as "taken").
            const stateClass = edge
              ? 'bg-gold text-espresso-deep font-semibold'
              : inRange
                ? 'bg-gold/20 text-espresso'
                : isBlocked
                  ? 'bg-blush-soft/60 text-blush-deep/80 line-through'
                  : dis
                    ? 'text-muted/40 line-through'
                    : 'text-espresso';
            return (
              <button key={i} type="button" disabled={dis} onClick={() => pick(d)}
                className={[
                  'relative aspect-square rounded-full text-sm transition',
                  stateClass,
                  dis ? 'cursor-not-allowed' : 'hover:bg-gold/15',
                ].join(' ')}>
                {d.getDate()}
                {isBlocked && (
                  <span className="pointer-events-none absolute right-1 top-1 text-[9px] leading-none text-blush-deep/70" aria-hidden>×</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-16">
      {/* Room selector (multi-select — book several rooms on one set of dates) */}
      <p className="mb-3 font-body text-xs uppercase tracking-wide text-muted">Choose your room{selectedRooms.length === 1 ? '' : 's'} — tap to add or remove</p>
      <div className="grid gap-3 sm:grid-cols-3">
        {ROOMS.map((r) => {
          const active = selectedIds.includes(r.id);
          return (
            <button key={r.id} type="button" onClick={() => toggleRoom(r.id)}
              className={`relative border p-4 text-left transition ${active ? 'border-gold bg-gold/10' : 'border-line bg-cream hover:border-gold'}`}>
              {active && (
                <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-gold text-espresso-deep"><Check size={13} /></span>
              )}
              <p className="font-display text-lg text-espresso">{r.name}</p>
              <p className="font-body text-[11px] uppercase tracking-wide text-muted">{r.sleeps}</p>
              <p className="mt-2 font-display text-xl text-espresso">{formatPula(r.price)} <span className="font-body text-[11px] uppercase tracking-wide text-muted">/ night</span></p>
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
        {/* Calendar */}
        <div className="border border-line bg-cream p-6">
          <div className="mb-4 flex items-center justify-between">
            <button type="button" disabled={!canPrev} onClick={() => setMonth(addMonths(month, -1))}
              className="rounded-full p-2 text-espresso transition hover:bg-gold/15 disabled:opacity-30"><ChevronLeft size={18} /></button>
            <p className="font-body text-xs uppercase tracking-wide text-muted">
              {selectingEnd ? 'Select check-out' : 'Select check-in'}
            </p>
            <button type="button" onClick={() => setMonth(addMonths(month, 1))}
              className="rounded-full p-2 text-espresso transition hover:bg-gold/15"><ChevronRight size={18} /></button>
          </div>
          <div className="flex gap-8">
            {renderMonth(month)}
            <div className="hidden flex-1 md:block">{renderMonth(addMonths(month, 1))}</div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-line pt-3 font-body text-[11px] text-muted">
            <span className="flex items-center gap-1.5"><span className="inline-block h-3.5 w-3.5 rounded-full bg-gold" /> Selected</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-3.5 w-3.5 rounded-full bg-gold/20" /> In your range</span>
            <span className="flex items-center gap-1.5">
              <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-blush-soft/60 text-[8px] leading-none text-blush-deep/80 line-through">×</span> Unavailable
            </span>
          </div>
          <p className="mt-2 font-body text-[11px] text-muted">
            {selectedRooms.length > 1 ? 'A date is shown as unavailable if any chosen room is booked. ' : ''}
            Check-in {STAY_POLICY.checkIn.toLowerCase()} · check-out {STAY_POLICY.checkOut.toLowerCase()}.
          </p>
        </div>

        {/* Reserve card */}
        <aside className="h-fit border border-line bg-paper p-6 lg:sticky lg:top-28">
          <p className="font-display text-2xl text-espresso">{formatPula(rate)} <span className="font-body text-sm text-muted">/ night{selectedRooms.length > 1 ? ' total' : ''}</span></p>
          <div className="rule-gold mt-3" />

          {selectedRooms.length > 0 && (
            <ul className="mt-4 space-y-1.5">
              {selectedRooms.map((r) => (
                <li key={r.id} className="flex items-center justify-between font-body text-sm">
                  <span className="text-espresso">{r.name}</span>
                  <span className="text-muted">{formatPula(r.price)}<span className="text-[11px]"> /night</span></span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-5 grid grid-cols-2 border border-line">
            <div className="border-r border-line p-3">
              <p className="font-body text-[10px] uppercase tracking-wide text-muted">Check-in</p>
              <p className="font-body text-sm text-espresso">{niceDate(checkIn)}</p>
            </div>
            <div className="p-3">
              <p className="font-body text-[10px] uppercase tracking-wide text-muted">Check-out</p>
              <p className="font-body text-sm text-espresso">{niceDate(checkOut)}</p>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between border border-line p-3">
            <span className="font-body text-sm text-espresso">Guests</span>
            <span className="flex items-center gap-3">
              <button type="button" onClick={() => setGuests((g) => Math.max(1, g - 1))} className="rounded-full border border-line p-1 text-espresso hover:bg-gold/15"><Minus size={14} /></button>
              <span className="w-5 text-center font-body text-sm text-espresso">{guests}</span>
              <button type="button" onClick={() => setGuests((g) => Math.min(12, g + 1))} className="rounded-full border border-line p-1 text-espresso hover:bg-gold/15"><Plus size={14} /></button>
            </span>
          </div>

          <div className="mt-4 space-y-2.5">
            <input className="field !py-2.5" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name" />
            <input className="field !py-2.5" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
            <input className="field !py-2.5" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" />
            <input className="field !py-2.5" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else? (optional)" />
          </div>
          <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden className="absolute left-[-9999px] h-0 w-0 opacity-0" />

          {nights > 0 && (
            <div className="mt-5 space-y-2 border-t border-line pt-4 font-body text-sm">
              <div className="flex justify-between text-muted">
                <span>{formatPula(rate)} × {nights} night{nights === 1 ? '' : 's'}{selectedRooms.length > 1 ? ` · ${selectedRooms.length} rooms` : ''}</span>
                <span className="text-espresso">{formatPula(total)}</span>
              </div>
              <div className="flex justify-between border-t border-line pt-2 font-display text-lg text-espresso">
                <span>Total</span><span>{formatPula(total)}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-md border border-blush-deep/40 bg-blush-soft/40 p-3">
              <p className="font-body text-sm text-espresso">{error}</p>
              <a href={waLink(biz.whatsapp, waText)} target="_blank" rel="noreferrer" className="btn-gold mt-2.5 !py-2 !text-[11px]"><MessageCircle size={13} /> Book on WhatsApp</a>
            </div>
          )}

          <button onClick={openConfirm} disabled={busy} className="btn-primary mt-5 w-full">
            {busy ? 'Sending…' : 'Request to book'} <ArrowRight size={15} />
          </button>
          <p className="mt-3 flex items-center justify-center gap-1.5 font-body text-[11px] text-muted">
            <Check size={12} className="text-gold-deep" /> You won't be charged — we'll confirm availability by email.
          </p>
        </aside>
      </div>

      {confirming && (
        <Modal onClose={() => setConfirming(false)}>
            <h2 className="font-display text-2xl text-espresso">Confirm your stay</h2>
            <div className="rule-gold mt-3" />
            <div className="mt-5 space-y-2 font-body text-sm text-muted">
              <div className="flex justify-between"><span>{selectedRooms.length > 1 ? 'Rooms' : 'Room'}</span><span className="text-right text-espresso">{roomsLabel}</span></div>
              <div className="flex justify-between"><span>Check-in</span><span className="text-espresso">{niceDate(checkIn)} · {STAY_POLICY.checkIn.toLowerCase()}</span></div>
              <div className="flex justify-between"><span>Check-out</span><span className="text-espresso">{niceDate(checkOut)} · {STAY_POLICY.checkOut.toLowerCase()}</span></div>
              <div className="flex justify-between"><span>Guests</span><span className="text-espresso">{guests}</span></div>
              <div className="flex justify-between border-t border-line pt-2 font-display text-lg text-espresso"><span>{nights} night{nights === 1 ? '' : 's'}</span><span>{formatPula(total)}</span></div>
            </div>
            <div className="mt-4 rounded-md bg-paper p-3">
              <p className="font-body text-xs leading-relaxed text-muted">
                Please note our times — <strong className="text-espresso">check-in {STAY_POLICY.checkIn.toLowerCase()}</strong> and <strong className="text-espresso">check-out {STAY_POLICY.checkOut.toLowerCase()}</strong>. This is a request; we'll confirm availability by email and no payment is taken now.
              </p>
            </div>
            <div className="mt-6 flex gap-2.5">
              <button onClick={() => setConfirming(false)} className="btn-outline flex-1 justify-center">Back</button>
              <button onClick={submit} disabled={busy} className="btn-primary flex-1 justify-center">{busy ? 'Sending…' : 'Confirm & request'}</button>
            </div>
        </Modal>
      )}
    </div>
  );
}
